import {createReducer, on} from '@ngrx/store';
import {
  ClearAdultUsernameFilter,
  ClearClientFilter,
  ClearParticipantUsernameFilter,
  ClearState,
  FetchAdultUsernameFilterFailure,
  FetchAdultUsernameFilterRequest,
  FetchAdultUsernameFilterSuccess,
  FetchClientFilterFailure,
  FetchClientFilterListSuccess,
  FetchClientFilterRequest,
  FetchEmailPreviewDataFailure,
  FetchEmailPreviewDataRequest,
  FetchEmailPreviewDataSuccess,
  FetchGroupFilterFailure,
  FetchGroupFilterSuccess,
  FetchGroupsFilterRequest,
  FetchParticipantUsernameFilterFailure,
  FetchParticipantUsernameFilterRequest,
  FetchParticipantUsernameFilterSuccess,
  HasAssociatedLarFailure,
  HasAssociatedLarRequest,
  HasAssociatedLarSuccess,
  InviteUserFailure,
  InviteUserRequest,
  InviteUserSuccess,
  SetEmailPreviewData,
} from "./invite.actions";
import {Filter} from '../../../../models/filter';
import {EmailPreviewDataModel} from "../models/email-preview-data.model";

// State for this feature (User)
export interface InviteState {
  groupFilter: Filter,
  clientFilter: Filter,
  rolesFilter: Filter,
  participantUsernameFilter: Filter,
  adultUsernameFilter: Filter,
  inviteUser: {
    loading: boolean
  },
  participantHasAssociatedLar: {
    loading: boolean,
    hasAssociatedLar: boolean
  }
  emailPreviewDataInfo: {
    emailPreviewData: EmailPreviewDataModel[];
    loading: boolean,
  },
  createUserDataInfo: {
    emailPreviewData: EmailPreviewDataModel[];
  }
}

const initialState: InviteState = {
  groupFilter: {
    list: [],
    total: null,
    pageNumber: 1,
    limit: 10,
    hasNext: false,
    hasPrev: false,
    loading: false
  },
  clientFilter: {
    list: [],
    total: null,
    pageNumber: 1,
    limit: 10,
    hasNext: false,
    hasPrev: false,
    loading: false
  },
  rolesFilter: {
    list: [],
    total: null,
    pageNumber: 1,
    limit: 10,
    hasNext: false,
    hasPrev: false,
    loading: false
  },
  participantUsernameFilter: {
    list: [],
    total: null,
    pageNumber: 1,
    limit: 10,
    hasNext: false,
    hasPrev: false,
    loading: false
  },
  adultUsernameFilter: {
    list: [],
    total: null,
    pageNumber: 1,
    limit: 10,
    hasNext: false,
    hasPrev: false,
    loading: false
  },
  inviteUser: {
    loading: false
  },
  participantHasAssociatedLar: {
    loading: false,
    hasAssociatedLar: false
  },
  emailPreviewDataInfo: {
    emailPreviewData: [],
    loading: false,
  },
  createUserDataInfo: {
    emailPreviewData: []
  }
};

export const reducer = createReducer(
  initialState,

  // CLEAR STATE
  on(ClearState, (state) => initialState),

  // FETCH GROUP FILTER LIST
  on(FetchGroupsFilterRequest, (state, props) => ({
    ...state,
    groupFilter: {
      ...initialState.groupFilter,
      ...(props.payload.pageNumber ? props.payload.pageNumber > 1 ? {list: state.groupFilter.list} : {list: []} : {list: []}),
      loading: true
    }
  })),
  on(FetchGroupFilterSuccess, (state, props) => ({
    ...state,
    groupFilter: {
      ...props.groupFilter,
      ...(props.groupFilter.pageNumber ? props.groupFilter.pageNumber > 1 ? {
        list: [
          ...state.groupFilter.list, ...props.groupFilter.list
        ]
      } : {list: props.groupFilter.list} : {list: []}),
      loading: false
    }
  })),
  on(FetchGroupFilterFailure, (state) => ({
    ...state,
    groupFilter: {
      ...state.groupFilter,
      loading: false
    }
  })),

  // FETCH CLIENT FILTER LIST
  on(FetchClientFilterRequest, (state) => ({
    ...state,
    clientFilter: {
      ...initialState.clientFilter,
      loading: true
    }
  })),
  on(FetchClientFilterListSuccess, (state, props) => ({
    ...state,
    clientFilter: {
      ...props.clientFilter,
      loading: false
    }
  })),
  on(FetchClientFilterFailure, (state) => ({
    ...state,
    clientFilter: {
      ...state.clientFilter,
      loading: false
    }
  })),

  // FETCH PARTICIPANT USERNAME FILTER
  on(FetchParticipantUsernameFilterRequest, (state) => ({
    ...state,
    participantUsernameFilter: {
      ...initialState.participantUsernameFilter,
      loading: true
    }
  })),
  on(FetchParticipantUsernameFilterSuccess, (state, props) => ({
    ...state,
    participantUsernameFilter: {
      ...props.participantUsernameFilter,
      loading: false
    }
  })),
  on(FetchParticipantUsernameFilterFailure, (state) => ({
    ...state,
    participantUsernameFilter: {
      ...state.participantUsernameFilter,
      loading: false
    }
  })),

  // FETCH ADULT USERNAME FILTER
  on(FetchAdultUsernameFilterRequest, (state) => ({
    ...state,
    adultUsernameFilter: {
      ...initialState.adultUsernameFilter,
      loading: true
    }
  })),
  on(FetchAdultUsernameFilterSuccess, (state, props) => ({
    ...state,
    adultUsernameFilter: {
      ...props.adultUsernameFilter,
      loading: false
    }
  })),
  on(FetchAdultUsernameFilterFailure, (state) => ({
    ...state,
    adultUsernameFilter: {
      ...state.adultUsernameFilter,
      loading: false
    }
  })),

  // INVITE USER
  on(InviteUserRequest, (state) => ({
    ...state,
    inviteUser: {
      loading: true
    }
  })),
  on(InviteUserSuccess, (state, props) => ({
    ...state,
    inviteUser: {
      loading: false
    }
  })),
  on(InviteUserFailure, (state) => ({
    ...state,
    inviteUser: {
      loading: false
    }
  })),

  // HAS ASSOCIATED LAR
  on(HasAssociatedLarRequest, (state) => ({
    ...state,
    participantHasAssociatedLar: {
      ...initialState.participantHasAssociatedLar,
      loading: true
    }
  })),
  on(HasAssociatedLarSuccess, (state, props) => ({
    ...state,
    participantHasAssociatedLar: {
      hasAssociatedLar: props.hasAssociatedLar,
      loading: false
    }
  })),
  on(HasAssociatedLarFailure, (state) => ({
    ...state,
    participantHasAssociatedLar: {
      ...state.participantHasAssociatedLar,
      loading: false
    }
  })),

  // FETCH EMAIL PREVIEW DATA
  on(FetchEmailPreviewDataRequest, (state) => ({
    ...state,
    emailPreviewDataInfo: {
      emailPreviewData: initialState.emailPreviewDataInfo.emailPreviewData,
      loading: true
    }
  })),
  on(FetchEmailPreviewDataSuccess, (state, props) => ({
    ...state,
    emailPreviewDataInfo: {
      emailPreviewData: props.emailPreviewData,
      loading: false
    }
  })),
  on(FetchEmailPreviewDataFailure, (state) => ({
    ...state,
    emailPreviewDataInfo: {
      emailPreviewData: initialState.emailPreviewDataInfo.emailPreviewData,
      loading: false
    }
  })),

  // CLEAR PARTICIPANT USERNAME FILTER
  on(ClearParticipantUsernameFilter, (state) => ({
    ...state,
    participantUsernameFilter: {
      ...initialState.participantUsernameFilter
    }
  })),

  // CLEAR ADULT USERNAME FILTER
  on(ClearAdultUsernameFilter, (state) => ({
    ...state,
    adultUsernameFilter: {
      ...initialState.adultUsernameFilter
    }
  })),

  // CLEAR CLIENT FILTER
  on(ClearClientFilter, (state) => ({
    ...state,
    clientFilter: {
      ...initialState.clientFilter,
    }
  })),

  // SET EMAIL PREVIEW DATA
  on(SetEmailPreviewData, (state, props) => ({
    ...initialState,
    createUserDataInfo: {
      emailPreviewData: props.emailPreviewData
    }
  })),
);
